import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "@phosphor-icons/react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MarqueeBand from "../components/MarqueeBand";
import GroupCard from "../components/GroupCard";
import SocialTile from "../components/SocialTile";
import ScrollProgress from "../components/ScrollProgress";
import CursorHalo from "../components/CursorHalo";
import MagneticButton from "../components/MagneticButton";
import SeoulClock from "../components/SeoulClock";
import RotatingWord from "../components/RotatingWord";
import HeroReel from "../components/HeroReel";
import MoodPlayer from "../components/MoodPlayer";
import KoreanDrift from "../components/KoreanDrift";
import ProductShowcase from "../components/ProductShowcase";
import AboutSection from "../components/AboutSection";
import { api, CATEGORY_GROUPS } from "../lib/api";
import { toast } from "sonner";

// Real-character "reel" frames — cycle in sync with the rotating headline word.
// Hangang Global brand imagery (Korean lifestyle, wellness & K-beauty).
const REEL_FRAMES = [
  {
    src: "https://customer-assets.emergentagent.com/job_hangang-vibe/artifacts/xgli647p_ChatGPT%20Image%20May%2024%2C%202026%2C%2009_42_14%20PM.png",
    label: "Beauty.",
    kicker: "Frame 01",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_hangang-vibe/artifacts/m1ue420o_ChatGPT%20Image%20May%2024%2C%202026%2C%2009_55_42%20PM.png",
    label: "Indulgence.",
    kicker: "Frame 02",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_hangang-vibe/artifacts/2n4brhwt_ChatGPT%20Image%20May%2024%2C%202026%2C%2009_50_37%20PM.png",
    label: "Glow.",
    kicker: "Frame 03",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_hangang-vibe/artifacts/k7ivz6f0_ChatGPT%20Image%20May%2024%2C%202026%2C%2010_04_32%20PM.png",
    label: "Ritual.",
    kicker: "Frame 04",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_hangang-vibe/artifacts/035w5z3u_ChatGPT%20Image%20May%2024%2C%202026%2C%2010_26_54%20PM.png",
    label: "Lifestyle.",
    kicker: "Frame 05",
  },
];

const HEADLINE_WORDS = ["Beauty.", "Indulgence.", "Glow.", "Ritual.", "Lifestyle."];

const MOODS = [
  { slug: "beverages", label: "Soju o'clock", sublabel: "Beverages · Nightlife pours", emoji: "🍶", bg: "linear-gradient(120deg, rgba(255,107,158,0.16), rgba(244,63,94,0.10))" },
  { slug: "beauty",    label: "Glow loading", sublabel: "Beauty · Skin care",         emoji: "✨", bg: "linear-gradient(120deg, rgba(167,139,250,0.16), rgba(56,189,248,0.10))" },
  { slug: "beauty",    label: "Beauty ritual", sublabel: "Beauty · Cosmetics & care", emoji: "💋", bg: "linear-gradient(120deg, rgba(255,107,158,0.16), rgba(167,139,250,0.10))" },
  { slug: "wellness",  label: "After hours",   sublabel: "Wellness · Recovery mode",  emoji: "🌙", bg: "linear-gradient(120deg, rgba(56,189,248,0.16), rgba(52,211,153,0.10))" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [activeGroup, setActiveGroup] = useState("all");
  const [form, setForm] = useState({ name: "", company: "", country: "", email: "", interest_category: "Beauty", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const reelScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const reelY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const headlineY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const heroFade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const groupCounts = (() => {
    const map = {};
    CATEGORY_GROUPS.forEach((g) => {
      map[g.slug] = products.filter((p) => g.categories.includes(p.category)).length;
    });
    return map;
  })();

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Sent. We'll be in touch.");
      setForm({ name: "", company: "", country: "", email: "", interest_category: "Beauty", message: "" });
    } catch {
      toast.error("Could not submit. Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <CursorHalo />
      <ScrollProgress />
      <Navbar />

      {/* HERO — clean white with color splashes, real-face reel, mood player */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Soft drifting color blobs on white */}
        <div className="bg-orbs" aria-hidden>
          <div className="orb orb-c" />
        </div>

        {/* Korean Hangul drift — integrated typography, no chrome */}
        <KoreanDrift count={7} seed={1} />

        {/* MoodPlayer floating — replaces the static stickers */}
        <div className="absolute bottom-10 left-6 md:left-10 z-20 hidden md:block">
          <MoodPlayer
            moods={MOODS}
            interval={2800}
            onPick={(m) => { setActiveGroup(m.slug); document.getElementById("catalogue")?.scrollIntoView({ behavior: "smooth" }); }}
          />
        </div>

        <div className="max-w-7xl mx-auto w-full px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center pt-24 pb-16">
          <motion.div style={{ opacity: heroFade, y: headlineY }} className="lg:col-span-7 relative">
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="mb-10"
            >
              <SeoulClock />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-light tracking-[-0.04em] leading-[0.92] text-[3rem] sm:text-[4.5rem] lg:text-[7rem] xl:text-[8rem]"
              data-testid="hero-headline"
            >
              <span className="block">Korean</span>
              <RotatingWord
                words={HEADLINE_WORDS}
                interval={3200}
                duration={0.85}
                stagger={0.05}
                className="font-medium"
              />
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.6 }}
              className="mt-10 text-[15px] md:text-base text-slate-500 max-w-md leading-relaxed"
            >
              A studio for Korean-made lifestyle — in motion.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-8 flex items-center gap-4"
            >
              <MagneticButton as="a" href="#catalogue" className="btn-pill-primary" data-testid="hero-cta-enter">
                Enter the catalogue <ArrowUpRight size={14} weight="bold" />
              </MagneticButton>
              <a href="#categories" className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-slate-500 hover:text-slate-900 transition-colors">
                Explore <ArrowDown size={14} weight="bold" />
              </a>
            </motion.div>
          </motion.div>

          {/* HERO PHOTO REEL — real characters cycling with the headline word */}
          <div className="lg:col-span-5 relative flex items-center justify-center">
            <motion.div style={{ scale: reelScale, y: reelY }} className="w-full">
              <HeroReel frames={REEL_FRAMES} interval={3200} />
            </motion.div>
          </div>
        </div>

        {/* scroll hint */}
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-slate-500"
        >
          Scroll <ArrowDown size={12} weight="bold" />
        </motion.div>
      </section>

      <MarqueeBand items={["Studio Drops", "K-Beauty", "Wellness", "Ritual", "Glow", "Catalogue Nº 01"]} />

      {/* CATEGORIES — 4 group cards */}
      <section id="categories" className="py-24 md:py-32 px-5 md:px-8" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12 md:mb-16">
            <div>
              <span className="label-uppercase text-[#FF6B9E]">01 · The Lineup</span>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mt-3 leading-[1.05] max-w-xl">
                Four worlds, <span className="font-serif italic font-normal">one</span> studio.
              </h2>
            </div>
            <p className="md:max-w-sm text-[14px] text-slate-500 leading-relaxed">
              Korean-made categories that move with culture.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {CATEGORY_GROUPS.map((g, i) => (
              <GroupCard
                key={g.slug}
                group={g}
                index={i}
                count={groupCounts[g.slug] || 0}
                onClick={() => {
                  setActiveGroup(g.slug);
                  document.getElementById("catalogue")?.scrollIntoView({ behavior: "smooth" });
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CATALOGUE — futuristic cinema strip */}
      <ProductShowcase
        products={products}
        activeGroup={activeGroup}
        onGroupChange={setActiveGroup}
      />

      <MarqueeBand items={["#KLifestyle", "#GlowUp", "#SeoulMood", "#Ritual", "#StudioDrops"]} />

      {/* ABOUT */}
      <AboutSection />

      {/* SOCIAL FEED */}
      <section id="social" className="py-24 md:py-32 px-5 md:px-8 bg-[#0F172A] text-white relative overflow-hidden" data-testid="social-section">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-10 left-1/4 w-80 h-80 bg-[#FF6B9E] rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-[#A78BFA] rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
            <div>
              <span className="label-uppercase text-[#34D399]">04 · Live</span>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mt-3 leading-[1.05]">
                From the feed, <span className="font-serif italic font-normal">in real time</span>
              </h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              {["Instagram", "TikTok", "YouTube", "LinkedIn"].map((s) => (
                <span key={s} className="sticker bg-white/5 text-white border-white/20">{s}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="social-grid">
            {products.slice(0, 8).map((p, i) => <SocialTile key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-32 px-5 md:px-8 holo-bg" data-testid="contact-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="label-uppercase text-[#FF6B9E]">05 · Partnership</span>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mt-3 leading-[1.05]">
              Bring K-culture <span className="font-serif italic font-normal">to your shelves</span>.
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-sm md:text-[15px]">
              For importers, distributors, retailers and lifestyle partners.
            </p>
          </div>

          <form onSubmit={onSubmit} className="glass rounded-3xl p-6 md:p-10 grid md:grid-cols-2 gap-5" data-testid="contact-form">
            <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
            <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="contact-company" />
            <Field label="Country" required value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="contact-country" />
            <Field label="Email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
            <div className="md:col-span-2">
              <label className="label-uppercase block mb-2">Interest Category</label>
              <select
                data-testid="contact-interest"
                value={form.interest_category}
                onChange={(e) => setForm({ ...form, interest_category: e.target.value })}
                className="w-full bg-white/80 border border-slate-200 rounded-2xl px-4 py-3 focus:border-[#FF6B9E] focus:outline-none font-medium"
              >
                {["Beverages", "Beauty", "Wellness", "Beauty Devices", "All / General"].map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="label-uppercase block mb-2">Message *</label>
              <textarea
                required
                data-testid="contact-message"
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-white/80 border border-slate-200 rounded-2xl px-4 py-3 focus:border-[#FF6B9E] focus:outline-none font-medium resize-none"
                placeholder="Tell us about your market, your moodboard…"
              />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
              <p className="text-xs text-slate-500">We reply within 48h, in your timezone.</p>
              <MagneticButton as="button" type="submit" disabled={submitting} data-testid="contact-submit" className="btn-pill-primary disabled:opacity-60">
                {submitting ? "Sending…" : "Send Inquiry"} <ArrowUpRight size={14} weight="bold" />
              </MagneticButton>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="label-uppercase block mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        required={required}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/80 border border-slate-200 rounded-2xl px-4 py-3 focus:border-[#FF6B9E] focus:outline-none font-medium"
      />
    </div>
  );
}
